/**
 * useXlsxParser Hook
 * Parses XLSX files and extracts user identifiers
 */
import { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { COLUMN_MAPPINGS } from "../types";
/**
 * Hook for parsing XLSX files containing user identifiers
 */
export function useXlsxParser() {
    const [records, setRecords] = useState([]);
    const [columnMapping, setColumnMapping] = useState({
        v5UserIdColumn: null,
        identityIdColumn: null,
    });
    const [headers, setHeaders] = useState([]);
    const [isParsing, setIsParsing] = useState(false);
    const [error, setError] = useState(null);
    /**
     * Find column index by matching against known variations (case-insensitive)
     */
    const findColumn = useCallback((headers, variations) => {
        const lowerVariations = variations.map((v) => v.toLowerCase());
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i]?.trim().toLowerCase();
            if (header && lowerVariations.includes(header)) {
                return i;
            }
        }
        return null;
    }, []);
    /**
     * Parse XLSX file and extract user records
     */
    const parseFile = useCallback(async (file) => {
        setIsParsing(true);
        setError(null);
        try {
            // Read file as array buffer
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            // Get first sheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            if (jsonData.length === 0) {
                setError("File is empty");
                setIsParsing(false);
                return null;
            }
            // Extract headers (first row)
            const fileHeaders = jsonData[0];
            setHeaders(fileHeaders);
            // Find columns
            const v5UserIdColumn = findColumn(fileHeaders, COLUMN_MAPPINGS.v5UserId);
            const identityIdColumn = findColumn(fileHeaders, COLUMN_MAPPINGS.identityId);
            // Validate we have at least one identifier column
            if (v5UserIdColumn === null && identityIdColumn === null) {
                setError("Could not find V5UserId or IdentityId column. Please ensure your file has a column named 'V5UserId', 'User Id', 'IdentityId', or similar.");
                setIsParsing(false);
                return null;
            }
            const mapping = {
                v5UserIdColumn,
                identityIdColumn,
            };
            setColumnMapping(mapping);
            // Extract user records (skip header row)
            const userRecords = [];
            for (let i = 1; i < jsonData.length; i++) {
                const row = jsonData[i];
                if (!row || row.length === 0)
                    continue;
                // Get V5UserId if column exists
                let v5UserId = null;
                if (v5UserIdColumn !== null && row[v5UserIdColumn]) {
                    v5UserId = String(row[v5UserIdColumn]).trim();
                }
                // Get IdentityId if column exists
                let identityId = null;
                if (identityIdColumn !== null && row[identityIdColumn]) {
                    identityId = String(row[identityIdColumn]).trim();
                }
                // Skip rows with no identifiers
                if (!v5UserId && !identityId)
                    continue;
                userRecords.push({
                    rowNumber: i + 1, // Excel row number (1-indexed, accounting for header)
                    v5UserId,
                    identityId,
                    displayName: null,
                    upn: null,
                    status: "pending",
                    error: null,
                    skipReason: null,
                });
            }
            if (userRecords.length === 0) {
                setError("No valid user records found in file");
                setIsParsing(false);
                return null;
            }
            setRecords(userRecords);
            setIsParsing(false);
            return { records: userRecords, columnMapping: mapping };
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to parse file";
            setError(errorMessage);
            setIsParsing(false);
            return null;
        }
    }, [findColumn]);
    /**
     * Clear parser state
     */
    const clear = useCallback(() => {
        setRecords([]);
        setColumnMapping({ v5UserIdColumn: null, identityIdColumn: null });
        setHeaders([]);
        setError(null);
    }, []);
    return {
        records,
        columnMapping,
        headers,
        isParsing,
        error,
        parseFile,
        clear,
    };
}
